import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AuthDescription } from '@/components/features/auth/auth-description';
import { LoginForm } from '@/components/features/auth/forms/login-form';
import { LoginOrSignupWith } from '@/components/features/auth/login-or-signup-with';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { PublicPage } from '@/components/ui/layout/public-page';
import { getPreviewOrganizationQuery } from '@/queries/getPreviewOrganizationQuery';

import { invitationSearchSchema } from '../invitation';

const emailSearchSchema = z.object({
  email: z.string().email().optional(),
  redirect: z.string().optional(),
});

const searchSchema = invitationSearchSchema.merge(emailSearchSchema);

export const Route = createFileRoute('/_public/login')({
  component: Login,
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: ({ context }) => {
    const isAuthenticated = context.isAuthenticated;

    if (isAuthenticated) {
      throw redirect({ to: '/dashboard' });
    }
  },
});

export function Login() {
  const search = Route.useSearch();
  const { t } = useTranslation();

  const { data: previewOrganization } = useQuery(
    getPreviewOrganizationQuery({
      organizationId: search.organizationId,
      invitationCode: search.invitationCode,
    })
  );

  return (
    <PublicPage.Layout>
      <PublicPage.Header />
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl text-center">
            {t('auth.login.title')}
          </CardTitle>
          <AuthDescription
            type="login"
            organizationName={previewOrganization?.name}
          />
        </CardHeader>

        <CardContent>
          <LoginOrSignupWith
            type="login"
            redirect={search.redirect}
            invitationFlow={{
              invitationCode: search.invitationCode,
              organizationId: search.organizationId,
            }}
          />
          <LoginForm
            email={search.email}
            redirect={search.redirect}
            invitationFlow={{
              invitationCode: search.invitationCode,
              organizationId: search.organizationId,
              invitationEmail: previewOrganization?.invitationEmail,
            }}
          />
        </CardContent>
        <CardFooter className="flex justify-center pt-4">
          <p className="text-sm text-muted-foreground">
            {t('auth.layout.footer.missingAccount')}{' '}
            <Link
              to="/signup"
              className="text-primary hover:underline font-medium"
              search={search}
            >
              {t('auth.layout.footer.signup')}
            </Link>
          </p>
        </CardFooter>
      </Card>
      <PublicPage.Footer />
    </PublicPage.Layout>
  );
}
